import cx from 'classnames';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FunctionComponent,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from 'react';
import FocusLock from 'react-focus-lock';

import CrossIcon from '@atlaskit/icon/core/cross';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { useExitingPersistence } from '@atlaskit/motion/exiting-persistence/use-exiting-persistence';
import { Reanimate } from '@atlaskit/motion/motion';
import type { Transition } from '@atlaskit/motion/types';
import { useMotion } from '@atlaskit/motion/use-motion';
import { intl } from '@trello/i18n';
import { Layers, type Layer } from '@trello/layer-manager';
import { token, type IconColor } from '@trello/theme';

import { Button } from '../Button';
import type { GlyphProps } from '../Icon';
import { Overlay, type OverlayAlignment, type OverlayEffect } from '../Overlay';
import { DIALOG_LIVE_REGION_ID } from './dialogLiveRegion';

import * as styles from './Dialog.module.less';

/**
 * Callback function fired when Dialog is shown
 * @callback onShowCallback
 */

/**
 * Callback function fired when Dialog is shown
 * @callback onHideCallback
 */

/**
 * @typedef UseDialogHookConfigOptions
 * @type {object}
 * @property {onShowCallback} onShow
 * @property {onHideCallback} onHide
 */
interface UseDialogHookConfigOptions {
  onShow?: () => void;
  onHide?: () => void;
}

type UseDialogHookResult = {
  show: () => void;
  hide: () => void;
  toggle: () => void;
  isOpen: boolean;
  dialogProps: {
    show: () => void;
    hide: () => void;
    toggle: () => void;
    isOpen: boolean;
  };
};

/**
 * useDialog React hook. Used to manage show/hide state for a Dialog component
 *
 * @usage
 * ```js
 * const { show, dialogProps } = useDialog({
 *   onShow: () => console.log('dialog was shown'),
 *   onHide: () => console.log('dialog was hidden'),
 * });
 *
 * return (
 *  <div>
 *    <button onClick={show}>
 *      Click me to show the dialog!
 *    </button>
 *    <Dialog title="Example dialog" {...dialogProps}>
 *      I am a dialog
 *    </Dialog>
 *  </div>
 * );
 * ```
 *
 * @function useDialog
 * @param {UseDialogHookConfigOptions} [opts]
 */
export const useDialog = (
  opts?: UseDialogHookConfigOptions,
): UseDialogHookResult => {
  const { onShow, onHide } = opts ?? {};

  const [isOpen, setIsOpen] = useState(false);
  const show = useCallback(() => {
    setIsOpen(true);
    onShow?.();
  }, [setIsOpen, onShow]);

  const hide = useCallback(() => {
    setIsOpen(false);
    onHide?.();
  }, [setIsOpen, onHide]);

  const toggle = useCallback(() => {
    if (isOpen) {
      hide();
    } else {
      show();
    }
  }, [isOpen, show, hide]);

  return {
    show,
    hide,
    toggle,
    isOpen,
    dialogProps: {
      show,
      hide,
      toggle,
      isOpen,
    },
  };
};

interface DialogCloseButtonProps {
  className?: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  size?: GlyphProps['size'];
  /**
   * Set the color of the CloseIcon component.
   *
   * @default token('color.text.accent.gray.bolder')
   */
  iconColor?: IconColor;
}

const defaultIconColor = token('color.text.accent.gray.bolder', '#091E42');

const dialogMotionTokens = {
  entering: token('motion.modal.enter', 'none'),
  exiting: token('motion.modal.exit', 'none'),
} as const;

const blanketMotionTokens = {
  entering: token('motion.blanket.enter', 'none'),
  exiting: token('motion.blanket.exit', 'none'),
} as const;

type DialogMotionRenderProps = {
  blanketAnimation?: string;
  dialogAnimation?: string;
  isExiting: boolean;
};

type DialogMotionProps = {
  children: (props: DialogMotionRenderProps) => ReactNode;
  dialogRef: RefObject<HTMLDivElement | null>;
  onCloseComplete?: () => void;
  shouldAnimate: boolean;
};

const DialogMotion: FunctionComponent<DialogMotionProps> = ({
  children,
  dialogRef,
  onCloseComplete,
  shouldAnimate,
}) => {
  const { isExiting, onFinish } = useExitingPersistence();
  const hasFinishedRef = useRef(false);
  const finishClose = useCallback(() => {
    if (!hasFinishedRef.current) {
      hasFinishedRef.current = true;
      onCloseComplete?.();
    }
  }, [onCloseComplete]);
  const handleMotionFinish = useCallback(
    (state: Transition) => {
      if (state === 'exiting') {
        finishClose();
      }
    },
    [finishClose],
  );
  const {
    ref: motionRef,
    reanimate,
    state,
  } = useMotion<HTMLDivElement>({
    onFinish: handleMotionFinish,
  });

  useLayoutEffect(() => {
    motionRef(dialogRef.current);
    return () => motionRef(null);
  }, [dialogRef, motionRef]);

  useEffect(() => {
    if (!isExiting && state === 'exiting') {
      reanimate(Reanimate.enter);
    }
  }, [isExiting, reanimate, state]);

  useEffect(() => {
    if (isExiting && !shouldAnimate) {
      onFinish?.();
      finishClose();
    }
  }, [finishClose, isExiting, onFinish, shouldAnimate]);

  const dialogAnimation =
    shouldAnimate && (state === 'entering' || state === 'exiting')
      ? dialogMotionTokens[state]
      : undefined;
  const blanketAnimation =
    shouldAnimate && (state === 'entering' || state === 'exiting')
      ? blanketMotionTokens[state]
      : undefined;

  return children({ blanketAnimation, dialogAnimation, isExiting });
};

export const DialogCloseButton: FunctionComponent<DialogCloseButtonProps> = ({
  className,
  onClick,
  size = 'large',
  iconColor = defaultIconColor,
}) => (
  <Button
    aria-label={intl.formatMessage({
      id: 'templates.close dialog',
      defaultMessage: 'Close dialog',
      description: 'Accessible label for the close button on a dialog',
    })}
    iconBefore={
      <span
        className={cx({
          [styles.largeIcon]: size === 'large',
        })}
      >
        <CrossIcon label="" color={iconColor} />
      </span>
    }
    className={cx({
      [styles.closeButton]: true,
      [String(className)]: !!className,
    })}
    onClick={onClick}
    style={{ color: iconColor }}
  />
);

export interface DialogProps {
  /**
   * Optional classname applied to root Dialog container element
   */
  className?: string;

  /**
   * Width of the Dialog. Defaults to "medium"
   */
  size?: 'large' | 'medium' | 'small' | 'xlarge';

  /**
   * Contents of the Dialog
   */
  children: ReactNode;

  /**
   * Whether the Dialog is open. Provided by `useDialog` hook via `dialogProps`
   */
  isOpen?: boolean;

  /**
   * Callback function to show the Dialog. Provided by useDialog hook via `dialogProps`
   */
  show: () => void;

  /**
   * Callback function to hide the Dialog. Provided by useDialog hook via `dialogProps`
   */
  hide: () => void;

  /**
   * Callback function to toggle the Dialog. Provided by useDialog hook via `dialogProps`
   */
  toggle: () => void;

  /**
   * Should this Dialog close when the Escape key is pressed?
   */
  closeOnEscape?: boolean;

  /**
   * Should this Dialog close when the outside of the Dialog is clicked?
   */
  closeOnOutsideClick?: boolean;

  /**
   * Specify the alignment of the dialog.
   *
   * @default 'top'
   */
  alignment?: OverlayAlignment;

  /**
   * Visual effect of the overlay/blanket.
   *
   * @default 'default'
   */
  effect?: OverlayEffect;

  /**
   * Defines the the layer to render the dialog in.
   * Was added as an exception for the Card Back
   */
  layer?: Layer;

  /**
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby
   */
  labelledBy?: string;

  /**
   * The unique identifier for the FocusLock component.
   * Used to group focus lock instances together.
   */
  focusLockGroup?: string;

  /**
   * Called after an open Dialog has finished closing.
   */
  onCloseComplete?: () => void;

  /**
   * Whether to animate this Dialog's current open session.
   *
   * @default false
   */
  shouldAnimate?: boolean;
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      className,
      size = 'medium',
      isOpen,
      show,
      hide,
      children,
      closeOnEscape = true,
      closeOnOutsideClick = true,
      alignment = 'top',
      effect,
      layer = Layers.Overlay,
      labelledBy,
      focusLockGroup,
      onCloseComplete,
      shouldAnimate = false,
    },
    forwardedRef,
  ) => {
    const isBlurEnabled = document.body.classList.contains('overlay-blur');
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const setDialogRef = useCallback(
      (node: HTMLDivElement | null) => {
        dialogRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const overlayEffect = (effect ?? isBlurEnabled) ? 'blur' : 'default';
    const wasOpenRef = useRef(false);
    const shouldAnimateSessionRef = useRef(false);
    const hasCompletedImmediateCloseRef = useRef(false);
    const isOpening = Boolean(isOpen) && !wasOpenRef.current;
    const isClosing = !isOpen && wasOpenRef.current;

    if (isOpening) {
      shouldAnimateSessionRef.current = shouldAnimate;
      hasCompletedImmediateCloseRef.current = false;
    } else if (isClosing && !shouldAnimate) {
      shouldAnimateSessionRef.current = false;
    }

    useEffect(() => {
      wasOpenRef.current = Boolean(isOpen);
    }, [isOpen]);

    const hasMotionSession = shouldAnimateSessionRef.current;
    const shouldAnimateCurrentSession = hasMotionSession && shouldAnimate;

    useLayoutEffect(() => {
      if (
        isClosing &&
        !hasMotionSession &&
        !hasCompletedImmediateCloseRef.current
      ) {
        hasCompletedImmediateCloseRef.current = true;
        onCloseComplete?.();
      }
    }, [hasMotionSession, isClosing, onCloseComplete]);

    const renderDialog = ({
      blanketAnimation,
      dialogAnimation,
      isExiting,
    }: DialogMotionRenderProps) => (
      <Overlay
        alignment={alignment}
        animation={blanketAnimation}
        effect={overlayEffect}
        closeOnEscape={closeOnEscape}
        closeOnOutsideClick={closeOnOutsideClick}
        isExiting={isExiting}
        onClose={hide}
        layer={layer}
      >
        <FocusLock group={focusLockGroup} returnFocus={true} autoFocus>
          <div
            className={cx({
              [styles.dialog]: true,
              [String(className)]: !!className,
              [styles[size]]: !!size,
            })}
            tabIndex={-1}
            ref={setDialogRef}
            role="dialog"
            aria-modal
            aria-labelledby={labelledBy}
            data-testid={labelledBy}
            style={{ animation: dialogAnimation }}
          >
            <div className={styles.body}>{children}</div>
            <div
              id={DIALOG_LIVE_REGION_ID}
              className={styles.liveRegion}
              aria-live="polite"
              aria-atomic="true"
            />
          </div>
        </FocusLock>
      </Overlay>
    );

    if (!hasMotionSession) {
      return isOpen ? renderDialog({ isExiting: false }) : null;
    }

    return (
      <ExitingPersistence appear>
        {isOpen ? (
          <DialogMotion
            key="dialog"
            dialogRef={dialogRef}
            onCloseComplete={onCloseComplete}
            shouldAnimate={shouldAnimateCurrentSession}
          >
            {renderDialog}
          </DialogMotion>
        ) : null}
      </ExitingPersistence>
    );
  },
);
